import { defineStore } from 'pinia';
import { 
    getContacts, 
    getContactsByLetter, 
    fetchContactById, 
    createContact, 
    updateContact, 
    deleteContact
} from '@/services/contactService';

export const useContactStore = defineStore('contacts', {
    state: () => ({
        contacts: [],
        loading: false,
        error: null,
        pagination: {
            current_page: 1,
            total: 0,
            per_page: 50
        }
    }),
    actions: {
        async loadContacts(page = 1, perPage = 50) {
            this.loading = true;
            this.error = null;
            try {
                const response = await getContacts(page, perPage);
                console.log('API Response:', response);
                
                // Backend returns: { success: true, data: [...], current_page, per_page, total, last_page }
                if (response.success && Array.isArray(response.data)) {
                    this.contacts = response.data;
                    this.pagination = {
                        current_page: response.current_page || page,
                        total: response.total || response.data.length,
                        per_page: response.per_page || perPage,
                        last_page: response.last_page || 1
                    };
                } else if (Array.isArray(response.data)) {
                    // Fallback for direct data array
                    this.contacts = response.data;
                    this.pagination = {
                        current_page: response.current_page || page,
                        total: response.total || response.data.length,
                        per_page: response.per_page || perPage,
                        last_page: response.last_page || 1
                    };
                } else {
                    this.contacts = [];
                    console.warn('Unexpected response format:', response);
                }
                
                console.log('Processed contacts:', this.contacts);
            } catch (error) {
                console.error('Error loading contacts:', error);
                this.error = error.response?.data?.message || error.message || 'Failed to load contacts';
                this.contacts = [];
            } finally {
                this.loading = false;
            }
        },
        async loadContactsByLetter(letter = null) {
            this.loading = true;
            this.error = null;
            try {
                const response = await getContactsByLetter(letter);
                console.log('API Response by letter:', response);
                
                // Backend returns: { success: true, data: [...] }
                if (response.success && Array.isArray(response.data)) {
                    this.contacts = response.data;
                } else if (Array.isArray(response.data)) {
                    // Fallback for direct data array
                    this.contacts = response.data;
                } else {
                    this.contacts = [];
                    console.warn('Unexpected response format:', response);
                }
                
                console.log('Processed contacts by letter:', this.contacts);
                
                // Return the processed contacts for the alphabet filter component
                return this.contacts;
            } catch (error) {
                console.error('Error loading contacts by letter:', error);
                this.error = error.response?.data?.message || error.message || 'Failed to load contacts';
                this.contacts = [];
                return [];
            } finally {
                this.loading = false;
            }
        },
        async getContactById(id) {
            try {
                const response = await fetchContactById(id);
                // Backend returns: { success: true, data: {...} }
                if (response.success && response.data) {
                    return response.data;
                } else if (response.data) {
                    // Fallback for direct data
                    return response.data;
                }
                throw new Error('Invalid response format');
            } catch (error) {
                console.error('Error fetching contact by ID:', error);
                throw error;
            }
        },
        async updateContact(updatedContact) {
            try {
                const response = await updateContact(updatedContact.id, updatedContact);
                // Backend returns: { success: true, message: "...", data: {...} }
                const contact = response.data || response;
                
                // Update in local state if found
                const index = this.contacts.findIndex((c) => c.id === contact.id);
                if (index !== -1) {
                    this.contacts[index] = contact;
                }
                return contact;
            } catch (error) {
                console.error('Error updating contact:', error);
                throw error;
            }
        },
        async deleteContact(id) {
            try {
                await deleteContact(id);
                // Remove from local state
                this.contacts = this.contacts.filter((c) => c.id !== id);
            } catch (error) {
                console.error('Error deleting contact:', error);
                throw error;
            }
        },
        async createContact(contactData) {
            try {
                const response = await createContact(contactData);
                // Backend returns: { success: true, message: "...", data: {...} }
                const contact = response.data || response;
                
                // Add to local state
                this.contacts.push(contact);
                return contact;
            } catch (error) {
                console.error('Error creating contact:', error);
                throw error;
            }
        }
    },
});
